import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  DNABox,
  DNAButton,
  DNADivider,
  DNASelect,
  DNAText,
  DNASpinner,
  TextField,
} from '@alucio/lux-ui';
import DNADocumentSummaryContent from 'src/components/DNA/Document/DNADocumentSummaryContent';
import { useAllDocumentVersionMap } from 'src/state/redux/selector/document';
import { contentPreviewModalActions } from 'src/state/redux/slice/contentPreviewModal';
import {
  generateMantleDocumentSummaryDebug,
  getMantleSummaryDebugModels,
  MantleSummaryDebugModel,
  MantleSummaryDebugQuote,
  MantleSummaryDebugResult,
  quoteMantleDocumentSummaryDebug,
} from './mantleSummaryDebugApi';
import { S } from '..';

const formatUsd = (value: number | null | undefined): string => (
  typeof value === 'number' ? `$${value.toFixed(6)}` : 'Price unavailable'
);

const formatModelPrice = (model?: MantleSummaryDebugModel): string => {
  if (!model?.priceAvailable) return 'Price unavailable';

  return [
    `$${(model.inputPriceUsdPer1MTokens ?? 0).toFixed(4)} input`,
    `$${(model.outputPriceUsdPer1MTokens ?? 0).toFixed(4)} output`,
    'per 1M tokens',
  ].join(' · ');
};

const formatComparisonBaseline = (previousDocumentVersionNumber: number | null | undefined): string => (
  typeof previousDocumentVersionNumber === 'number'
    ? `Compared with published version ${previousDocumentVersionNumber}.`
    : 'No previous published version is available for comparison.'
);

const getErrorMessage = (error: unknown): string => (
  error instanceof Error ? error.message : 'The request could not be completed.'
);

type SelectIndex = Exclude<React.ComponentProps<typeof DNASelect>['selectedIndex'], undefined>;
type StatusMessage = {
  text: string;
  status: 'danger' | 'success';
};

const DEFAULT_SYSTEM_PROMPT = [
  'Create a concise, well-structured Markdown summary of this document.',
  'Preserve key facts, warnings, headings, and tables when present.',
].join('\n');

const styles = StyleSheet.create({
  summaryPreview: {
    maxHeight: 480,
  },
  systemPromptInput: {
    minHeight: 112,
  },
});

const MantleSummaryDebug: React.FC = () => {
  const documentVersionMap = useAllDocumentVersionMap();
  const dispatch = useDispatch();
  const documentVersions = useMemo(
    () => Object.values(documentVersionMap).sort((first, second) =>
      (first.model.title ?? first.model.id).localeCompare(second.model.title ?? second.model.id)),
    [documentVersionMap],
  );
  const [models, setModels] = useState<MantleSummaryDebugModel[]>([]);
  const [selectedDocumentIndex, setSelectedDocumentIndex] = useState<number>();
  const [selectedModelId, setSelectedModelId] = useState<string>();
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_SYSTEM_PROMPT);
  const [quote, setQuote] = useState<MantleSummaryDebugQuote>();
  const [result, setResult] = useState<MantleSummaryDebugResult>();
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [message, setMessage] = useState<StatusMessage>();

  const selectedDocument = selectedDocumentIndex === undefined
    ? undefined
    : documentVersions[selectedDocumentIndex];
  const sortedModels = useMemo(
    () => [...models].sort((first, second) => first.id.localeCompare(second.id, undefined, {
      sensitivity: 'base',
    })),
    [models],
  );
  const selectedModel = sortedModels.find(({ id }) => id === selectedModelId);
  const hasSystemPrompt = Boolean(systemPrompt.trim());
  const hasCurrentQuote = Boolean(
    quote &&
    quote.documentVersionId === selectedDocument?.model.id &&
    quote.modelId === selectedModel?.id,
  );

  const loadModels = useCallback(async () => {
    setIsLoadingModels(true);
    setMessage(undefined);
    try {
      setModels(await getMantleSummaryDebugModels());
    } catch (error: unknown) {
      setMessage({ text: getErrorMessage(error), status: 'danger' });
    } finally {
      setIsLoadingModels(false);
    }
  }, []);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  const onSelectDocument = (index: SelectIndex) => {
    if (Array.isArray(index)) return;
    setSelectedDocumentIndex(index.row);
    setQuote(undefined);
    setResult(undefined);
    setMessage(undefined);
  };

  const onSelectModel = (index: SelectIndex) => {
    if (Array.isArray(index)) return;
    setSelectedModelId(sortedModels[index.row]?.id);
    setQuote(undefined);
    setResult(undefined);
    setMessage(undefined);
  };

  const onChangeSystemPrompt = (value: string) => {
    setSystemPrompt(value);
    setQuote(undefined);
    setResult(undefined);
    setMessage(undefined);
  };

  const previewSelectedDocument = () => {
    if (!selectedDocument) return;

    dispatch(contentPreviewModalActions.setModalVisibility({
      documentVersionId: selectedDocument.model.id,
      content: selectedDocument,
      isOpen: true,
      isFullWindow: false,
    }));
  };

  const requestQuote = async () => {
    if (!selectedDocument || !selectedModel) return;
    if (!hasSystemPrompt) {
      setMessage({ text: 'System prompt is required.', status: 'danger' });
      return;
    }

    setIsWorking(true);
    setMessage(undefined);
    try {
      setQuote(await quoteMantleDocumentSummaryDebug(
        selectedDocument.model.id,
        selectedModel.id,
        systemPrompt,
      ));
    } catch (error: unknown) {
      setMessage({ text: getErrorMessage(error), status: 'danger' });
    } finally {
      setIsWorking(false);
    }
  };

  const generateSummary = async () => {
    if (!selectedDocument || !selectedModel || !hasCurrentQuote || !quote) return;
    const quoteToken = quote.quoteToken;

    setIsWorking(true);
    setMessage(undefined);
    try {
      setResult(await generateMantleDocumentSummaryDebug(
        selectedDocument.model.id,
        selectedModel.id,
        quoteToken,
        systemPrompt,
      ));
    } catch (error: unknown) {
      setMessage({ text: getErrorMessage(error), status: 'danger' });
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <ScrollView contentContainerStyle={S.tabContainer}>
      <DNABox appearance="col" spacing="lg">
        <DNABox appearance="col" spacing="xs">
          <DNAText h3>Mantle document summary</DNAText>
          <DNAText status="tertiary">
            Choose any available Mantle model, review the estimate, then generate a Markdown summary.
          </DNAText>
        </DNABox>

        <DNABox appearance="col" spacing="sm">
          <DNAText b2 bold>Document</DNAText>
          <DNASelect
            disabled={isWorking}
            placeholder="Select a document version"
            value={selectedDocument
              ? `${selectedDocument.model.title ?? selectedDocument.model.id} (${selectedDocument.model.id})`
              : undefined}
            selectedIndex={selectedDocumentIndex === undefined ? undefined : new DNASelect.Index(selectedDocumentIndex)}
            onSelect={onSelectDocument}
          >
            {documentVersions.map((documentVersion) => (
              <DNASelect.Item
                key={documentVersion.model.id}
                title={`${documentVersion.model.title ?? documentVersion.model.id} (${documentVersion.model.id})`}
              />
            ))}
          </DNASelect>
          <DNAButton
            appearance="outline"
            disabled={!selectedDocument || isWorking}
            onPress={previewSelectedDocument}
          >
            Preview selected document
          </DNAButton>
        </DNABox>

        <DNABox appearance="col" spacing="sm">
          <DNABox appearance="row" spacing="sm" alignY="center">
            <DNAText b2 bold>Mantle model</DNAText>
            {isLoadingModels && <DNASpinner size="small" />}
          </DNABox>
          <DNASelect
            disabled={isLoadingModels || !sortedModels.length || isWorking}
            placeholder="Select a Mantle model"
            value={selectedModel?.id}
            selectedIndex={selectedModel === undefined
              ? undefined
              : new DNASelect.Index(sortedModels.findIndex(({ id }) => id === selectedModelId))}
            onSelect={onSelectModel}
          >
            {sortedModels.map((model) => (
              <DNASelect.Item key={model.id} title={model.id} />
            ))}
          </DNASelect>
          <DNAText status="tertiary">{formatModelPrice(selectedModel)}</DNAText>
        </DNABox>

        <DNABox appearance="col" spacing="sm">
          <DNAText b2 bold>System prompt</DNAText>
          <TextField.Kitten
            disabled={isWorking}
            multiline
            onChangeText={onChangeSystemPrompt}
            placeholder="Describe how the document should be summarized"
            style={styles.systemPromptInput}
            value={systemPrompt}
          />
        </DNABox>

        <DNABox appearance="row" spacing="sm">
          <DNAButton
            appearance="outline"
            disabled={!selectedDocument || !selectedModel || !hasSystemPrompt || isWorking}
            onPress={requestQuote}
          >
            Get estimate
          </DNAButton>
          <DNAButton
            disabled={!hasCurrentQuote || isWorking}
            onPress={generateSummary}
          >
            Generate summary
          </DNAButton>
        </DNABox>

        {quote && (
          <DNABox appearance="col" spacing="xs">
            <DNAText b2 bold>Estimated request</DNAText>
            <DNAText>
              {`${quote.estimatedInputTokens} input tokens + up to ${quote.maxOutputTokens} output tokens`}
            </DNAText>
            <DNAText status="tertiary">
              {formatComparisonBaseline(quote.previousDocumentVersionNumber)}
            </DNAText>
            <DNAText>{`Maximum estimated cost: ${formatUsd(quote.estimatedMaxCostUsd)}`}</DNAText>
          </DNABox>
        )}

        {message && <DNAText status={message.status}>{message.text}</DNAText>}

        {result && (
          <DNABox appearance="col" spacing="md">
            <DNADivider />
            <DNABox appearance="col" spacing="xs">
              <DNAText h4>Generated Markdown preview</DNAText>
              <DNAText status="tertiary">
                {[
                  `${result.inputTokens} input tokens`,
                  `${result.outputTokens} output tokens`,
                  `Actual cost: ${formatUsd(result.actualCostUsd)}`,
                ].join(' · ')}
              </DNAText>
              <DNAText status="tertiary">
                {formatComparisonBaseline(result.previousDocumentVersionNumber)}
              </DNAText>
            </DNABox>
            <ScrollView
              nestedScrollEnabled
              showsVerticalScrollIndicator
              style={styles.summaryPreview}
            >
              <DNADocumentSummaryContent summary={result.summary} />
            </ScrollView>
          </DNABox>
        )}
      </DNABox>
    </ScrollView>
  );
};

export default MantleSummaryDebug;
